import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { api } from "@/lib/api-client";
import { UpdateGlobalPaymentSettings } from "@/server-types";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { useEffect, useState } from "react";
import { HiOutlineExternalLink } from "react-icons/hi";
import { useSearchParams } from "react-router";
import { StripePageHeader } from "./Components/StripePageHeader";
import { returnCurrencySymbol } from "../../../../../shared/utils/utils";
import useFeature from "@/hooks/useFeature";
import {
  addToast,
  Alert,
  Button,
  Chip,
  Divider,
  Link,
  Skeleton,
  Tab,
  Tabs,
} from "@heroui/react";
import { Check, ChevronRight, X } from "lucide-react";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";

export const AccountSettings = () => {
  const [searchParams, _] = useSearchParams();
  const isRedirectedFromCompletedOnboarding =
    searchParams.get("onboarding") === "1";

  const { isFeatureEnabled } = useFeature();

  const {
    data: accountData,
    refetch: refetchAccountData,
    processedErrorMessage: fetchAccountErrorMessage,
  } = api.stripe.useGetStripeAccount();

  const {
    isLoading: isLoadingOnboardingData,
    isRefetching,
    refetch: continueOnBoarding,
  } = api.stripe.useGetStripeAccountOnboardingUrl();
  const isLoadingOnboarding = isLoadingOnboardingData || isRefetching;

  const {
    mutateAsync: updateGlobalPaymentSetting,
    isPending: isUpdatingGlobalPaymentSetting,
  } = api.stripe.useUpdateStripePaymentSettingsGlobal();

  const {
    data: paymentSettings,
    isFetching: isPendingPaymentSettings,
    processedErrorMessage: fetchPaymentSettingsErrorMessage,
  } = api.stripe.useGetStripePaymentSettings();

  const [afterOnboardingTimerActive, setAfterOnboardingTimerActive] =
    useState<boolean>(false);
  useEffect(() => {
    if (!isRedirectedFromCompletedOnboarding) return;

    const interval = setInterval(() => {
      console.log("end interval");
      refetchAccountData();

      setAfterOnboardingTimerActive(false);
    }, 10000);

    // setActiveTab("settings");
    setAfterOnboardingTimerActive(true);

    return () => clearInterval(interval);
  }, []);

  const globalPaymentForm = useForm({
    initialValues: {
      globalPaymentPriceAmount: 0,
      globalPaymentPriceType: "percentage",
      isPaymentDefaultCheckedInDashboard: false,
      stripePaymentTimeout: 0,
      requirePaymentForNewCustomers: true,
      requirePaymentForExistingCustomers: true,
    } as UpdateGlobalPaymentSettings["body"]["updateData"],
    validate: {
      globalPaymentPriceAmount: (value) => {
        if (value < 0) {
          return t`Cena mora biti večja od 0`;
        }

        if (
          globalPaymentForm.values.globalPaymentPriceType === "percentage" &&
          value > 100
        ) {
          return t`Cena ne sme presegati 100%`;
        }
      },
    },
  });

  useEffect(() => {
    if (!paymentSettings) {
      return;
    }

    const data: (typeof globalPaymentForm)["values"] = {
      globalPaymentPriceAmount: paymentSettings.global.paymentPriceAmount ?? 0,
      globalPaymentPriceType:
        paymentSettings.global.paymentPriceType ?? "percentage",
      isPaymentDefaultCheckedInDashboard:
        paymentSettings.isPaymentDefaultCheckedInDashboard ?? true,
      stripePaymentTimeout: paymentSettings.global.stripePaymentTimeout ?? 1,
      requirePaymentForNewCustomers:
        paymentSettings.global.requirePaymentForNewCustomers ?? true,
      requirePaymentForExistingCustomers:
        paymentSettings.global.requirePaymentForExistingCustomers ?? true,
    };

    globalPaymentForm.setValues(data);
    globalPaymentForm.resetDirty(data);
  }, [paymentSettings]);

  const onContinueOnBoarding = () => {
    continueOnBoarding().then((res) => {
      if (res.data == null) {
        return;
      }

      window.location.href = res.data;
    });
  };

  const submitForm = async () => {
    /**
     * Only open dialog to update service settings if user changed
     * global amount or type
     */
    if (
      globalPaymentForm.isDirty("globalPaymentPriceAmount") ||
      globalPaymentForm.isDirty("globalPaymentPriceType")
    ) {
      modals.openConfirmModal({
        title: t`Želite posodobiti vrednosti tudi za nastavitve po meri?`,
        labels: {
          confirm: t`Da`,
          cancel: t`Ne`,
        },
        onCancel: async () => {
          await finishSubmittingForm({ updateCustomServices: false });
        },
        onConfirm: async () => {
          await finishSubmittingForm({ updateCustomServices: true });
        },
      });
    } else {
      await finishSubmittingForm({ updateCustomServices: false });
    }
  };

  const finishSubmittingForm = async ({
    updateCustomServices,
  }: {
    updateCustomServices: boolean;
  }) => {
    await updateGlobalPaymentSetting({
      updateData: globalPaymentForm.values,
      updateCustomServices,
    });

    addToast({
      title: t`Nastavitve so bile uspešno posodobljene`,
      color: "success",
    });

    refetchAccountData();
  };

  return (
    <div className="flex w-full flex-col items-start justify-start">
      <>
        <LimeAlert message={fetchAccountErrorMessage} />

        <p className="font-medium">
          <Trans>Podatki o Stripe računu</Trans>
        </p>
        <div className="my-2 flex flex-col gap-1">
          <div className="text-xs">
            <span className="inline-block w-12">
              <Trans>ID št.</Trans>:
            </span>{" "}
            <span className="text-gray-500">
              {accountData?.stripeAccountId}
            </span>
          </div>
          <div className="text-xs">
            <span className="inline-block w-12">
              <Trans>E-mail</Trans>:
            </span>{" "}
            <span className="text-gray-500">{accountData?.email}</span>
          </div>
        </div>

        <Chip
          variant="flat"
          color={accountData?.hasFinishedOnboarding ? "success" : "danger"}
          size="sm"
          endContent={
            accountData?.hasFinishedOnboarding ? (
              <Check size={14} />
            ) : (
              <X size={14} />
            )
          }
        >
          {accountData?.hasFinishedOnboarding
            ? t`Račun uspešno povezan`
            : t`Račun ni povezan`}
        </Chip>

        <Divider className="my-4" />

        <form
          onSubmit={globalPaymentForm.onSubmit(submitForm)}
          style={{ display: "contents" }}
        >
          <LimeAlert
            message={fetchPaymentSettingsErrorMessage}
            className="mb-2 w-full"
          />

          <StripePageHeader
            title={t`Globalne nastavitve`}
            subtitle={t`Določite znesek, ki ga morajo stranke plačati za potrditev termina. V primeru, da je predplačilo v evrih in presega ceno storitve, stranka plača 100% avans.`}
          />

          <div className="flex h-12 w-full items-end justify-between gap-4 md:justify-start">
            <Skeleton
              isLoaded={!isPendingPaymentSettings}
              className="rounded-xl"
            >
              <LimeNumberInput
                {...globalPaymentForm.getInputProps("globalPaymentPriceAmount")}
                isDisabled={!isFeatureEnabled("ADVANCED_PAYMENTS")}
                value={
                  isFeatureEnabled("ADVANCED_PAYMENTS")
                    ? globalPaymentForm.values.globalPaymentPriceAmount
                    : 100
                }
                size="sm"
                className="max-w-48"
                minValue={0}
                placeholder="0"
                maxValue={
                  globalPaymentForm.values.globalPaymentPriceType ===
                  "percentage"
                    ? 100
                    : undefined
                }
              />
            </Skeleton>
            <Skeleton
              isLoaded={!isPendingPaymentSettings}
              className="rounded-xl"
            >
              <Tabs
                selectedKey={
                  isFeatureEnabled("ADVANCED_PAYMENTS")
                    ? globalPaymentForm.values.globalPaymentPriceType
                    : "percentage"
                }
                onSelectionChange={(key) => {
                  globalPaymentForm.setFieldValue(
                    "globalPaymentPriceType",
                    key as "percentage" | "amount",
                  );
                }}
                classNames={{
                  tab: "h-10",
                }}
                isDisabled={!isFeatureEnabled("ADVANCED_PAYMENTS")}
              >
                <Tab key="percentage" title={"%"} />
                <Tab
                  key="amount"
                  title={returnCurrencySymbol({
                    currency: paymentSettings?.mainCurrency ?? "EUR",
                  })}
                />
              </Tabs>
            </Skeleton>
          </div>

          <Divider className="my-4" />

          <StripePageHeader title={t`Dodatne nastavitve`} />

          <Skeleton
            isLoaded={!isPendingPaymentSettings}
            className="w-full rounded-xl"
          >
            <LimeNumberInput
              {...globalPaymentForm.getInputProps("stripePaymentTimeout")}
              minValue={1}
              variant="bordered"
              size="sm"
              label={t`Privzeti čas za plačilo (min)`}
              description={t`Koliko časa ima stranka, da plača termin, preden plačilo poteče. Privzeta nastavitev, če ni določena na samem terminu.`}
            />
          </Skeleton>

          <div className="mt-4 flex w-full flex-col gap-2">
            <Skeleton
              isLoaded={!isPendingPaymentSettings}
              className="w-full rounded-xl"
            >
              <LimeSwitch
                className="w-full"
                title={t`Zahtevaj plačilo za nove stranke`}
                {...globalPaymentForm.getInputProps(
                  "requirePaymentForNewCustomers",
                  { type: "checkbox" },
                )}
                isDisabled={!isFeatureEnabled("PAYMENT_CUSTOMER_SEGMENTATION")}
                size="sm"
                isCompact
              />
            </Skeleton>

            <Skeleton
              isLoaded={!isPendingPaymentSettings}
              className="w-full rounded-xl"
            >
              <LimeSwitch
                className="w-full"
                title={t`Zahtevaj plačilo za obstoječe stranke`}
                {...globalPaymentForm.getInputProps(
                  "requirePaymentForExistingCustomers",
                  { type: "checkbox" },
                )}
                isDisabled={!isFeatureEnabled("PAYMENT_CUSTOMER_SEGMENTATION")}
                size="sm"
                isCompact
              />
            </Skeleton>
          </div>

          <Divider className="mt-4" />

          <Link
            href="/dashboard/stripe/settings/services"
            className="w-full py-4 text-foreground"
            isDisabled={
              !accountData?.hasFinishedOnboarding ||
              isLoadingOnboarding ||
              !isFeatureEnabled("ADVANCED_PAYMENTS")
            }
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col">
                <p className="text-sm">
                  <Trans>Nastavitve po meri - storitve</Trans>
                </p>
                <span className="text-xs text-gray-500">
                  <Trans>Določite znesek za vsako storitev po meri.</Trans>
                </span>
              </div>
              <ChevronRight size={20} />
            </div>
          </Link>

          <Divider className="mb-4" />

          <div className="w-full">
            <LimeSwitch
              className="w-full"
              title={t`Ali je potrebno plačilo ob dodajanju termina iz koledarja?`}
              {...globalPaymentForm.getInputProps(
                "isPaymentDefaultCheckedInDashboard",
                { type: "checkbox" },
              )}
              size="sm"
              isCompact
            ></LimeSwitch>
          </div>

          <Button
            type="submit"
            className="mt-6 w-full self-end md:w-auto"
            isLoading={isUpdatingGlobalPaymentSetting}
            isDisabled={!globalPaymentForm.isDirty()}
            color="primary"
          >
            <Trans>Shrani</Trans>
          </Button>
        </form>

        {(afterOnboardingTimerActive ||
          !accountData?.hasFinishedOnboarding) && (
          <Alert
            color={
              afterOnboardingTimerActive
                ? "warning"
                : accountData?.hasFinishedOnboarding
                  ? "success"
                  : "danger"
            }
            description={
              afterOnboardingTimerActive
                ? t`Povezava s Stripe računom se vzpostavlja. Prosimo počakajte trenutek.`
                : t`Povezavo s Stripe računom niste dokončali. Za nadaljevanje vzpostavitve spletnega plačevanja kliknite spodaj.`
            }
            title={
              afterOnboardingTimerActive
                ? t`Stripe račun se povezuje`
                : t`Stripe račun ni povezan`
            }
            variant="faded"
            className="mt-4"
          >
            <Button
              onPress={onContinueOnBoarding}
              endContent={<HiOutlineExternalLink />}
              isLoading={isLoadingOnboarding || afterOnboardingTimerActive}
              className="mt-2"
              variant="flat"
              color={
                afterOnboardingTimerActive
                  ? "warning"
                  : accountData?.hasFinishedOnboarding
                    ? "success"
                    : "danger"
              }
            >
              <Trans>Končaj povezavo</Trans>
            </Button>
          </Alert>
        )}
      </>
    </div>
  );
};
